/**
 * impacts.js — bullet impacts, ricochets and surface debris.
 *
 * Same three-layer thinking as gunfire (transient / body / tail), but here the layers
 * encode *material* rather than calibre:
 *
 *   - the transient's brightness says how hard the surface is,
 *   - the body says whether it resonates (metal, wood) or just absorbs (dirt, flesh),
 *   - the tail says whether it sheds debris (concrete dust, glass shards, water spray).
 *
 * Concrete and metal are the two the player hears most, so they get the most work.
 */

import {
  noiseSource, startNoise, noiseBurst, grains, perc, shape, biquad, sweep,
  waveshaper, gain, chain, partial, modal, rand,
} from './dsp.js';

/* ------------------------------------------------------------------ impacts --- */

function concrete(ac, dest, t, rng, lv) {
  // Sharp, dry crack; a short mid body; a dusty granular tail with a quick room slap.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: rand(rng, 2200, 3400), Q: 0.7,
    level: lv * 1.0, attack: 0.0003, decay: 0.004,
  });
  noiseBurst(ac, dest, t, rng, {
    freq: rand(rng, 620, 900), Q: 1.7, level: lv * 0.75, attack: 0.0008, decay: 0.045,
  });
  partial(ac, dest, t, rand(rng, 150, 210), lv * 0.4, 0.05, { glideTo: 110 });
  // Spalled dust and chips.
  grains(ac, dest, t + 0.012, rng, {
    count: 9, window: 0.11, loFreq: 1400, hiFreq: 7000, Q: 4,
    level: lv * 0.22, grainDecay: 0.01,
  });
  noiseBurst(ac, dest, t + 0.02, rng, {
    filter: 'lowpass', freq: 2600, Q: 0.7, level: lv * 0.2, attack: 0.006, decay: 0.16,
  });
}

function metal(ac, dest, t, rng, lv) {
  // The clang lives in the partials: strongly inharmonic ratios, long decays, and a
  // couple of very high ones that survive longest. Harmonic ratios would sound like
  // a bell; these sound like a struck plate.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: rand(rng, 3200, 5200), Q: 0.7,
    level: lv * 1.0, attack: 0.00025, decay: 0.0035,
  });
  const base = rand(rng, 900, 1500);
  modal(ac, dest, t, base, [
    [1.00, lv * 0.4, 0.34],
    [1.71, lv * 0.32, 0.28],
    [2.63, lv * 0.26, 0.22],
    [3.41, lv * 0.19, 0.17],
    [4.87, lv * 0.13, 0.12],
    [6.62, lv * 0.09, 0.09],
  ], { spread: 0.12, rng });
  // Impact "thud" under the ring so it has mass.
  partial(ac, dest, t, rand(rng, 170, 240), lv * 0.35, 0.055, { glideTo: 120 });
  noiseBurst(ac, dest, t, rng, {
    freq: base * 2.1, Q: 2.2, level: lv * 0.4, attack: 0.0006, decay: 0.035,
  });
}

function wood(ac, dest, t, rng, lv) {
  // Hollow knock: a strong low-mid resonance, a fast decay, and no HF sustain at all.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: rand(rng, 1300, 2100), Q: 0.7,
    level: lv * 0.7, attack: 0.0004, decay: 0.005,
  });
  const base = rand(rng, 260, 400);
  modal(ac, dest, t, base, [
    [1.00, lv * 0.75, 0.075],
    [2.42, lv * 0.3, 0.05],
    [4.13, lv * 0.14, 0.03],
  ], { spread: 0.07, rng });
  noiseBurst(ac, dest, t, rng, {
    filter: 'lowpass', freq: 3200, Q: 0.8, level: lv * 0.45, attack: 0.0006, decay: 0.03,
  });
  // Splinters.
  grains(ac, dest, t + 0.008, rng, {
    count: 5, window: 0.06, loFreq: 1800, hiFreq: 5000, Q: 5, level: lv * 0.12, grainDecay: 0.008,
  });
}

function dirt(ac, dest, t, rng, lv) {
  // No click at all: soil absorbs the transient. Just a soft low thud and a scatter
  // of loose particles falling back.
  noiseBurst(ac, dest, t, rng, {
    filter: 'lowpass', freq: rand(rng, 700, 1100), Q: 0.9,
    level: lv * 0.9, attack: 0.0015, decay: 0.055,
  });
  partial(ac, dest, t, rand(rng, 95, 135), lv * 0.5, 0.08, { attack: 0.003, glideTo: 70 });
  grains(ac, dest, t + 0.02, rng, {
    count: 11, window: 0.15, loFreq: 400, hiFreq: 2200, Q: 3.5, level: lv * 0.16, grainDecay: 0.014,
  });
}

function sand(ac, dest, t, rng, lv) {
  // Higher, drier and shorter than dirt, with almost no body.
  noiseBurst(ac, dest, t, rng, {
    filter: 'bandpass', freq: rand(rng, 1600, 2600), Q: 0.8,
    level: lv * 0.75, attack: 0.002, decay: 0.06,
  });
  grains(ac, dest, t + 0.01, rng, {
    count: 13, window: 0.1, loFreq: 1800, hiFreq: 6500, Q: 3, level: lv * 0.2, grainDecay: 0.009,
  });
  partial(ac, dest, t, rand(rng, 110, 150), lv * 0.2, 0.045, { attack: 0.004 });
}

function glass(ac, dest, t, rng, lv) {
  // Very bright transient, then a cloud of high inharmonic partials, then shards
  // arriving late and individually — the late arrivals sell it more than the break.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 6000, Q: 0.7, level: lv * 1.0, attack: 0.0002, decay: 0.004,
  });
  const base = rand(rng, 3000, 4200);
  modal(ac, dest, t, base, [
    [1.00, lv * 0.3, 0.19],
    [1.44, lv * 0.26, 0.16],
    [2.11, lv * 0.22, 0.13],
    [2.87, lv * 0.17, 0.1],
    [3.93, lv * 0.12, 0.08],
  ], { spread: 0.16, rng });
  for (let i = 0; i < 6; i++) {
    const at = t + rand(rng, 0.03, 0.34);
    const f = rand(rng, 2600, 8000);
    modal(ac, dest, at, f, [
      [1.0, lv * rand(rng, 0.05, 0.15), rand(rng, 0.03, 0.09)],
      [1.87, lv * rand(rng, 0.02, 0.07), rand(rng, 0.02, 0.05)],
    ], { spread: 0.2, rng });
  }
}

function flesh(ac, dest, t, rng, lv) {
  // Wet, dull, no ring. A lowpassed slap, a short pitched squelch sweeping down, and
  // a tiny spray of fine droplets.
  noiseBurst(ac, dest, t, rng, {
    filter: 'lowpass', freq: rand(rng, 900, 1400), Q: 1.1,
    level: lv * 0.95, attack: 0.001, decay: 0.05,
  });
  partial(ac, dest, t, rand(rng, 85, 120), lv * 0.6, 0.07, { glideTo: 55 });
  const sq = noiseSource(ac, 'pink');
  const bp = biquad(ac, 'bandpass', 1600, 2.4);
  sweep(bp.frequency, t, rand(rng, 1400, 2100), rand(rng, 380, 560), 0.09);
  const g = perc(ac, t, lv * 0.4, 0.004, 0.09);
  chain(sq, bp, g, dest);
  startNoise(sq, t, 0.14, rng);
  grains(ac, dest, t + 0.02, rng, {
    count: 6, window: 0.09, loFreq: 2200, hiFreq: 5500, Q: 6, level: lv * 0.09, grainDecay: 0.007,
  });
}

function water(ac, dest, t, rng, lv) {
  // Splash = a broadband burst whose centre sweeps *up* (the crown rising) followed
  // by droplets, each of which is a short sine chirping upward — that upward chirp is
  // the classic resonance of a bubble collapsing and is what the ear reads as "water".
  const src = noiseSource(ac, 'white');
  const bp = biquad(ac, 'bandpass', 700, 0.7);
  sweep(bp.frequency, t, rand(rng, 500, 800), rand(rng, 2600, 4000), 0.09);
  const g = shape(ac, t, [[0, 0], [0.006, lv * 0.9], [0.05, lv * 0.35], [0.16, 1e-5]]);
  chain(src, bp, g, dest);
  startNoise(src, t, 0.2, rng);
  partial(ac, dest, t, rand(rng, 130, 190), lv * 0.3, 0.06, { glideTo: 90, attack: 0.003 });
  for (let i = 0; i < 7; i++) {
    const at = t + rand(rng, 0.02, 0.3);
    const f = rand(rng, 700, 2600);
    partial(ac, dest, at, f, lv * rand(rng, 0.05, 0.16), rand(rng, 0.012, 0.035), {
      glideTo: f * rand(rng, 1.4, 2.4), attack: 0.001,
    });
  }
}

export const IMPACTS = { concrete, metal, wood, dirt, sand, glass, flesh, water };

export function impact(ac, dest, t, rng, material = 'concrete', level = 1) {
  const fn = IMPACTS[material] ?? IMPACTS.concrete;
  fn(ac, dest, t, rng, level * (1 + (rng() - 0.5) * 0.2));
}

/* ---------------------------------------------------------------- ricochet --- */

/**
 * The whine. A bullet deflecting off stone leaves spinning, and the Doppler of that
 * spin plus its own deformation gives a pitch that sweeps and wobbles. Two detuned
 * sawtooth voices through a tracking bandpass, with vibrato that speeds up as the
 * pitch falls, and a metallic strike at the front.
 */
export function ricochet(ac, dest, t, rng, level = 1) {
  const lv = level * (1 + (rng() - 0.5) * 0.2);
  const dur = rand(rng, 0.28, 0.5);
  const f0 = rand(rng, 1700, 3000);
  const f1 = f0 * rand(rng, 0.24, 0.42);

  // Strike.
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 3000, Q: 0.7, level: lv * 0.85, attack: 0.0003, decay: 0.005,
  });
  noiseBurst(ac, dest, t, rng, {
    freq: rand(rng, 800, 1300), Q: 1.6, level: lv * 0.45, attack: 0.0008, decay: 0.03,
  });

  const vib = ac.createOscillator();
  vib.type = 'sine';
  vib.frequency.setValueAtTime(rand(rng, 22, 38), t);
  vib.frequency.linearRampToValueAtTime(rand(rng, 55, 95), t + dur);
  const vibAmt = gain(ac, rand(rng, 45, 110));
  vib.connect(vibAmt);
  vib.start(t);
  vib.stop(t + dur + 0.05);

  const bp = biquad(ac, 'bandpass', f0, 3.2);
  sweep(bp.frequency, t, f0 * 1.4, f1 * 1.2, dur);
  const body = shape(ac, t, [[0, 0], [0.008, lv * 0.55], [dur * 0.45, lv * 0.3], [dur, 1e-5]]);
  const ws = waveshaper(ac, 1.6);

  for (let i = 0; i < 2; i++) {
    const o = ac.createOscillator();
    o.type = i === 0 ? 'sawtooth' : 'square';
    o.frequency.setValueAtTime(f0 * (1 + i * 0.005), t);
    o.frequency.exponentialRampToValueAtTime(f1 * (1 + i * 0.005), t + dur);
    vibAmt.connect(o.detune);
    const og = gain(ac, i === 0 ? 0.7 : 0.25);
    chain(o, og, bp);
    o.start(t);
    o.stop(t + dur + 0.02);
  }
  chain(bp, body, ws, dest);

  // Air/whistle component riding the same contour.
  const air = noiseSource(ac, 'white');
  const abp = biquad(ac, 'bandpass', f0, 9);
  sweep(abp.frequency, t, f0 * 1.4, f1 * 1.2, dur);
  const ag = shape(ac, t, [[0, 0], [0.02, lv * 0.3], [dur, 1e-5]]);
  chain(air, abp, ag, dest);
  startNoise(air, t, dur + 0.05, rng);
}

/* --------------------------------------------------------------- explosion --- */

/** Kept for grenades/barrels; also the source of the hearing-suppression trigger. */
export function explosion(ac, dest, t, rng, level = 1) {
  const lv = level;
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 1400, Q: 0.6, level: lv * 0.9, attack: 0.0004, decay: 0.02,
  });
  const o = ac.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(180, t);
  o.frequency.exponentialRampToValueAtTime(34, t + 0.5);
  const g = perc(ac, t, lv * 1.0, 0.004, 0.55);
  chain(o, g, waveshaper(ac, 3.4), gain(ac, 0.8), dest);
  o.start(t); o.stop(t + 0.7);
  const src = noiseSource(ac, 'brown');
  const lp = biquad(ac, 'lowpass', 2400, 0.8);
  sweep(lp.frequency, t, 3200, 320, 1.3);
  const ng = shape(ac, t, [[0, 0], [0.01, lv * 0.8], [0.25, lv * 0.35], [1.4, 1e-5]]);
  chain(src, lp, ng, dest);
  startNoise(src, t, 1.5, rng);
  grains(ac, dest, t + 0.08, rng, {
    count: 16, window: 0.7, loFreq: 600, hiFreq: 5000, Q: 4, level: lv * 0.12, grainDecay: 0.02,
  });
}
